import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, ArrowUpDown, ArrowUp, ArrowDown, Search, Filter as FilterIcon, ArrowUpRightFromSquare } from 'lucide-react';
import { Pagination } from '@/components/navigation/Pagination';
import {
  type IngestionFilter,
  type DateRangeInput,
} from '@/lib/api/system/ingestion-observability';
import {
  FetchMetricQuery,
  MetricCube,
  IngestionMeasure,
} from '@/lib/api/reporting/metrics';
import { MetricBreakdown } from '@/lib/api/types.gen';
import { Link } from 'react-router-dom';
import { routes } from '@/lib/routing/route-paths';

interface IngestionSiteHealthProps {
  tenantId: string;
  dateRange: DateRangeInput;
  filters: IngestionFilter;
  onSelectSite?: (projectId: string) => void;
}

interface SiteRow {
  id: string;
  name: string;
  customerId: string;
  customerName: string;
  eventCount: number;
  successCount: number;
  failureCount: number;
  filteredCount: number;
}

type SortField = 'name' | 'customerName' | 'eventCount' | 'successCount' | 'filteredCount' | 'failureCount';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 50;

export function IngestionSiteHealth({ tenantId, dateRange, filters, onSelectSite }: IngestionSiteHealthProps) {
  const [sortField, setSortField] = useState<SortField>('eventCount');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const dateStart = new Date(dateRange.dateStart);
  const dateEnd = new Date(dateRange.dateEnd);

  const { data: eventData, isLoading: eventsLoading, error } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.EventCount,
      breakdown: MetricBreakdown.Site,
      dateStart,
      dateEnd,
      siteId: filters.projectId,
      cameraId: filters.videoProcessorConfigId,
    })
  );

  const { data: successData, isLoading: successLoading } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.SuccessCount,
      breakdown: MetricBreakdown.Site,
      dateStart,
      dateEnd,
      siteId: filters.projectId,
      cameraId: filters.videoProcessorConfigId,
    })
  );

  const { data: failureData, isLoading: failureLoading } = useQuery(
    FetchMetricQuery({
      tenantId,
      cube: MetricCube.IngestionEvents,
      measure: IngestionMeasure.FailureCount,
      breakdown: MetricBreakdown.Site,
      dateStart,
      dateEnd,
      siteId: filters.projectId,
      cameraId: filters.videoProcessorConfigId,
    })
  );

  const isLoading = eventsLoading || successLoading || failureLoading;

  const sites = useMemo(() => {
    if (!eventData?.data) return [];

    const successMap = new Map<string, number>();
    if (successData?.data) {
      for (const point of successData.data) {
        const id = point.attributes?.['id'] ?? '';
        successMap.set(id, point.value ?? 0);
      }
    }

    const failureMap = new Map<string, number>();
    if (failureData?.data) {
      for (const point of failureData.data) {
        const id = point.attributes?.['id'] ?? '';
        failureMap.set(id, point.value ?? 0);
      }
    }

    const rows: SiteRow[] = eventData.data.map((point) => {
      const id = point.attributes?.['id'] ?? '';
      const eventCount = point.value ?? 0;
      const successCount = successMap.get(id) ?? 0;
      const failureCount = failureMap.get(id) ?? 0;
      const filteredCount = Math.max(0, eventCount - successCount - failureCount);

      return {
        id,
        name: point.attributes?.['name'] ?? point.attributes?.['id']?.slice(0, 8) ?? '',
        customerId: point.attributes?.['customerId'] ?? '',
        customerName: point.attributes?.['customerName'] ?? '',
        eventCount,
        successCount,
        failureCount,
        filteredCount,
      };
    });

    const needle = search.toLowerCase();
    const filtered = needle
      ? rows.filter((site) =>
          site.name.toLowerCase().includes(needle) ||
          site.customerName.toLowerCase().includes(needle)
        )
      : rows;

    return filtered.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      const cmp = typeof aVal === 'string'
        ? (aVal as string).localeCompare(bVal as string)
        : (aVal as number) - (bVal as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [eventData, successData, failureData, sortField, sortDir, search]);

  const totalPages = Math.max(1, Math.ceil(sites.length / PAGE_SIZE));
  const paginatedSites = sites.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 text-white/20" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 text-white/50" />
      : <ArrowDown className="h-3 w-3 text-white/50" />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-white/30" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-400">
        Failed to load site data: {error.message}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-[#2E3835] bg-[#1a1a1a] overflow-hidden">
      <div className="px-4 py-3 border-b border-[#2E3835] flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-medium text-white/80">Site Health</h3>
          <p className="text-xs text-white/30 mt-0.5">{sites.length} sites with activity</p>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-white/25" />
          <input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search sites..."
            className="h-7 w-[200px] rounded-md border border-[#2E3835] bg-transparent pl-7 pr-2 text-xs text-white/60 placeholder:text-white/25 outline-none focus:border-white/20"
          />
        </div>
      </div>

      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-[#2E3835] bg-white/[0.02] text-[10px] uppercase tracking-wider text-white/30">
            <th className="text-left px-3 py-2 font-medium">
              <button onClick={() => toggleSort('name')} className="inline-flex items-center gap-1">
                Site <SortIcon field="name" />
              </button>
            </th>
            <th className="text-left px-3 py-2 font-medium">
              <button onClick={() => toggleSort('customerName')} className="inline-flex items-center gap-1">
                Company <SortIcon field="customerName" />
              </button>
            </th>
            <th className="text-right px-3 py-2 font-medium">
              <button onClick={() => toggleSort('eventCount')} className="inline-flex items-center gap-1">
                Events <SortIcon field="eventCount" />
              </button>
            </th>
            <th className="text-right px-3 py-2 font-medium">
              <span className="text-emerald-400/60">Success</span>
            </th>
            <th className="text-right px-3 py-2 font-medium">
              <button onClick={() => toggleSort('filteredCount')} className="inline-flex items-center gap-1">
                <span className="text-amber-400/60">Filtered</span> <SortIcon field="filteredCount" />
              </button>
            </th>
            <th className="text-right px-3 py-2 font-medium">
              <button onClick={() => toggleSort('failureCount')} className="inline-flex items-center gap-1">
                <span className="text-red-400/60">Failed</span> <SortIcon field="failureCount" />
              </button>
            </th>
            <th className="text-right px-3 py-2 font-medium w-[80px]"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/[0.03]">
          {paginatedSites.map((site) => (
            <tr
              key={site.id}
              className="hover:bg-white/[0.02] transition-colors"
            >
              <td className="px-3 py-2">
                {site.customerId ? (
                  <Link
                    to={routes.customerSite({ customerId: site.customerId, siteId: site.id })}
                    target="_blank"
                    className="inline-flex items-center gap-1 truncate max-w-[200px] text-blue-400/70 hover:text-blue-400 hover:underline font-medium"
                    title={site.name}
                  >
                    {site.name}
                    <ArrowUpRightFromSquare className="h-2.5 w-2.5 shrink-0" />
                  </Link>
                ) : (
                  <div className="text-white/80 font-medium truncate max-w-[200px]" title={site.name}>
                    {site.name}
                  </div>
                )}
              </td>
              <td className="px-3 py-2">
                {site.customerName && site.customerId ? (
                  <Link
                    to={routes.customer({ customerId: site.customerId })}
                    target="_blank"
                    className="inline-flex items-center gap-1 truncate max-w-[160px] text-blue-400/70 hover:text-blue-400 hover:underline"
                    title={site.customerName}
                  >
                    {site.customerName}
                    <ArrowUpRightFromSquare className="h-2.5 w-2.5 shrink-0" />
                  </Link>
                ) : (
                  <div className="text-white/60 truncate max-w-[160px]" title={site.customerName}>
                    {site.customerName || '--'}
                  </div>
                )}
              </td>
              <td className="px-3 py-2 text-right font-mono text-white/60">
                {site.eventCount.toLocaleString()}
              </td>
              <td className="px-3 py-2 text-right font-mono text-emerald-400/70">
                {site.successCount.toLocaleString()}
              </td>
              <td className="px-3 py-2 text-right font-mono text-amber-400/70">
                {site.filteredCount.toLocaleString()}
              </td>
              <td className="px-3 py-2 text-right font-mono text-red-400/70">
                {site.failureCount > 0 ? site.failureCount.toLocaleString() : <span className="text-white/15">0</span>}
              </td>
              <td className="px-3 py-2 text-right">
                <button
                  onClick={() => onSelectSite?.(site.id)}
                  title="Filter ingestion logs to this site"
                  className="rounded p-1 text-white/30 hover:text-white/70 hover:bg-white/[0.05] border border-white/10 hover:border-white/20 transition-colors"
                >
                  <FilterIcon className="h-3 w-3" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {sites.length === 0 && (
        <div className="py-12 text-center text-xs text-white/30">
          {search ? 'No sites match your search' : 'No site data available for this period'}
        </div>
      )}

      {totalPages > 1 && (
        <div className="border-t border-[#2E3835]">
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}
